import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, reactive, ref, useContext, watch, watchEffect, } from '@nuxtjs/composition-api';
import { debounce, throttle } from 'throttle-debounce-ts';
import { useTask } from 'vue-concurrency';
import { ARTICLE_SIDEBAR_ITEMS } from './data';
import { FOOTER_INFO } from '~/constants';
// import { createIoConfig, setIoArticleType } from '~/plugins/ioAnalytics'
import { useDuck, useWaterfall } from '~/services';
import { cachePage, createAdfoxPuids, createArticleSchema, createFoldedArticleBody, createHead, createShareImage, formatArticle, randomNumber, sendAnalytics, shouldDisableArticleAd, useAsyncRef, } from '~/utils';
import { useA11y, useABTest, useArticlesHistory, useBookmarkStore, useSearch, useUser } from '~/composables';
import { useRecommendationSystem } from '~/composables/useRecommendationSystem';
/**
 * Duck service.
 */
const duck = useDuck();
const UNFOLDED_ARTICLES_QUANTITY = 5;
export default defineComponent({
    name: 'PageArticle',
    setup() {
        /**
         * Composables
         */
        const a11y = useA11y();
        const articlesHistory = useArticlesHistory();
        const bookmarkStore = useBookmarkStore();
        const context = useContext();
        const search = useSearch();
        const user = useUser();
        const waterfall = useWaterfall();
        /**
         * Context
         */
        const adfoxLoader = context.$adfoxLoader;
        const env = context.env;
        const viewport = context.$viewport;
        /**
         * Instance.
         */
        const instance = getCurrentInstance();
        /**
         * Page data.
         */
        const $page = useAsyncRef(instance, '$page');
        /**
         * Articles.
         */
        const articles = useAsyncRef(instance, 'articles');
        /**
         * Articles ref.
         */
        const articlesRef = ref([]);
        /**
         * Articles observer.
         */
        const articlesObserver = ref();
        /**
         * Articles offsets.
         */
        const articlesOffsets = ref([]);
        /**
         * Articles folded.
         */
        const articlesFolded = ref({
            0: false,
        });
        /**
         * Initial unfolded articles quantity
         */
        const unfoldedQuantity = ref(UNFOLDED_ARTICLES_QUANTITY);
        /**
         * Article current.
         */
        const articleCurrent = ref(0);
        const adfoxCompleteTimeout = ref(0);
        const isDone = ref(false);
        const lastScrollPosition = ref(0);
        const lastArticle = computed(() => articles.value[articles.value.length - 1]);
        const nextUrl = useAsyncRef(instance, 'nextUrl');
        const recommendation = useRecommendationSystem();
        const isTestA = computed(() => useABTest().tests.abRecommendationArticles === 'dsrkcbcntv');
        const trafficState = reactive({
            articleUrl: '',
            isShuffled: true,
        });
        const isGoogleBot = ref(false);
        if (process.client) {
            const agent = navigator.userAgent;
            const regex = /Google(?:bot|-InspectionTool)/;
            const found = agent.match(regex);
            isGoogleBot.value = !!found;
        }
        const fetchActualTask = useTask(function* (signal) {
            signal.addEventListener('abort', () => duck.abortRequest('getActualTask'));
            try {
                trafficState.articleUrl = '';
                let { data = [] } = yield duck.pub.getPubActual();
                data = data.filter((article) => !articlesHistory.data[article.url_alias]);
                const index = randomNumber(0, data.length - 1);
                trafficState.articleUrl = data[index]?.url_alias;
                if (!trafficState.articleUrl) {
                    trafficState.articleUrl = data[0]?.url_alias;
                }
            }
            catch { }
        });
        const fetchSpecialTask = useTask(function* (signal, isAdfoxError = false) {
            signal.addEventListener('abort', () => duck.abortRequest('getSpecialTask'));
            try {
                trafficState.articleUrl = '';
                const listId = isAdfoxError ? 'special-adblock' : `special-${context.route.value.params.rubric}`;
                let { data = [] } = yield duck.pub.getPubListById(listId);
                data = data.articles
                    .map(({ data }) => data)
                    .filter((article) => !articlesHistory.data[article.url_alias]);
                const index = randomNumber(0, data.length - 1);
                trafficState.articleUrl = data[index]?.url_alias;
                if (!trafficState.articleUrl) {
                    trafficState.articleUrl = data[0]?.url_alias;
                }
            }
            catch { }
        });
        /**
         * Articles sidebars
         */
        const articlesSidebars = Array.from({ length: unfoldedQuantity.value }, (_, i) => i).reduce((sidebars, i) => {
            sidebars[i] = reactive({
                isReady: false,
                items: [],
                itemsVisibility: [...ARTICLE_SIDEBAR_ITEMS.map(() => true)],
                totalHeight: 0,
            });
            return sidebars;
        }, {});
        /**
         * Fetch article task.
         */
        const fetchArticleTask = useTask(function* (signal, alias = '') {
            signal.addEventListener('abort', () => duck.abortRequest('getPubArticleByAlias'));
            let article;
            try {
                if (!trafficState.articleUrl) {
                    trafficState.isShuffled = false;
                }
                // Get article
                const urlAlias = alias || (trafficState.isShuffled ? trafficState.articleUrl || nextUrl.value : nextUrl.value);
                const { data } = isTestA.value
                    ? yield duck.pub.getPubArticleByAlias({ url_alias: urlAlias })
                    : yield useDuck()
                        .recommender.getRecommenderNodeUuid(recommendation?.uuid || '')
                        .catch(() => duck.pub.getPubArticleByAlias({ url_alias: urlAlias }));
                // Paywall
                try {
                    if (data.paywall && user.hasActiveSubscription) {
                        const { data: dataWithoutPaywall } = yield duck.paywall.getPaywallArticleId(String(data.id));
                        if (dataWithoutPaywall.paywall) {
                            Object.assign(data, dataWithoutPaywall);
                        }
                        else {
                            article = dataWithoutPaywall;
                        }
                    }
                }
                catch (error) { }
                // Format article
                article = formatArticle(article || data);
                // Brandvoice
                if (article.isBrandvoice) {
                    try {
                        const { data: tax } = yield duck.pub.getPubTax({
                            'list[limit]': 1,
                            'tax[url]': data.url_alias.replace(/\/\d+.+/g, ''),
                        });
                        article.tax = tax;
                    }
                    catch (error) {
                        console.error(`Failed to get tax`, error);
                    }
                }
                // Add/delete article from articles
                if (alias) {
                    const aliasIndex = articles.value.findIndex((a) => a.url_alias === alias);
                    if (aliasIndex !== -1) {
                        articles.value.splice(aliasIndex, 1, article);
                    }
                }
                else {
                    articles.value = [...articles.value, article];
                }
                const index = articles.value.length - 1;
                articlesFolded.value[index] = article.isGallery ? false : index >= unfoldedQuantity.value;
                if (trafficState.isShuffled) {
                    trafficState.isShuffled = false;
                }
                else {
                    nextUrl.value = article.next_url || article.rating?.next_url;
                    trafficState.isShuffled = !!trafficState.articleUrl;
                }
            }
            catch (error) {
                isDone.value = true;
                console.error(error);
            }
        });
        /**
         * Task for setting Adfox banners in sidebar
         */
        const setSidebarAdfoxTask = useTask(function* (_, timeout = 500) {
            yield new Promise((resolve) => setTimeout(resolve, timeout));
            if (articleCurrent.value >= unfoldedQuantity.value) {
                return;
            }
            const id = (articles.value[articleCurrent.value] || {}).id;
            const currentArticle = document.querySelector(`#article_${id}`);
            if (!currentArticle) {
                return console.error(`Article with id ${id} not found`);
            }
            const articleHeight = currentArticle.clientHeight;
            const defaultValues = Object.values(ARTICLE_SIDEBAR_ITEMS);
            const maxHeight = Math.max(...defaultValues.map(({ height = 0 }) => height));
            const currentSidebar = articlesSidebars[articleCurrent.value];
            // Reset current sidebar before next loop
            currentSidebar.items = [];
            currentSidebar.totalHeight = 0;
            currentSidebar.isReady = false;
            for (let i = 0, l = defaultValues.length; i < l; i++) {
                const { containerHeight } = ARTICLE_SIDEBAR_ITEMS[i];
                const isLastValue = i === defaultValues.length - 1;
                currentSidebar.items.push({ ...ARTICLE_SIDEBAR_ITEMS[i] });
                currentSidebar.totalHeight += containerHeight || 0;
                // Need to stop and recalc height
                if (currentSidebar.totalHeight > articleHeight - maxHeight || isLastValue) {
                    let fixedBannersHeight = 0;
                    const banners = Object.values(currentSidebar.items);
                    const stickyBanners = banners.filter(({ isSticky }) => isSticky);
                    const fixedBanners = banners.filter(({ isSticky }) => !isSticky);
                    if (stickyBanners.length) {
                        for (let i = 0, l = fixedBanners.length; i < l; i++) {
                            fixedBannersHeight += fixedBanners[i].containerHeight || 0;
                        }
                        const stickyBannerHeight = (articleHeight - fixedBannersHeight) / stickyBanners.length;
                        for (let i = 0, l = stickyBanners.length; i < l; i++) {
                            stickyBanners[i].containerHeight = stickyBannerHeight;
                        }
                    }
                    currentSidebar.isReady = true;
                    break;
                }
            }
        })
            .restartable()
            .maxConcurrency(1);
        /**
         * Handles loader "intersect" event.
         */
        function handleLoaderIntersect({ observer }) {
            if (fetchArticleTask.isRunning || isDone.value || !lastArticle.value) {
                return;
            }
            fetchArticleTask.perform();
            observer.disconnect();
        }
        const canLoadFeed = computed(() => {
            for (const entry of FOOTER_INFO) {
                if (entry.to.includes(context.route.value.path)) {
                    return false;
                }
            }
            return !isDone.value;
        });
        /**
         * Handles "scroll" event
         */
        const handleScroll = throttle(500, () => {
            for (let i = 0, l = articlesOffsets.value.length; i < l; i++) {
                const offset = articlesOffsets.value[i];
                if (window.scrollY <= offset) {
                    if (articleCurrent.value === i) {
                        return;
                    }
                    const closest = articles.value[i];
                    if (!closest) {
                        continue;
                    }
                    articleCurrent.value = i;
                    let erid = '';
                    let from = '';
                    let referer = document.referrer;
                    const title = closest.title || '';
                    const path = `/${closest.url_alias}${!isTestA.value ? '?utm_source=forbes&utm_campaign=new_feed' : ''}`;
                    let url = env.HOST + path;
                    if (title) {
                        document.title = title;
                        if (closest.erid) {
                            erid = `${path.includes('?') ? '&' : '?'}erid=${closest.erid}`;
                            url += erid;
                        }
                        history.replaceState(null, title, path + erid);
                        dispatchEvent(new CustomEvent('historyupdate'));
                        dispatchEvent(new CustomEvent('switchArticle', { detail: path }));
                    }
                    if (window.scrollY < lastScrollPosition.value) {
                        from = articles.value[i + 1]?.url_alias || '';
                    }
                    else {
                        // Downscroll
                        from = articles.value[i - 1]?.url_alias || '';
                    }
                    lastScrollPosition.value = window.scrollY;
                    if (from) {
                        referer = `${env.HOST}/${from}`;
                    }
                    sendAnalytics.call(context, {
                        path,
                        referer,
                        title,
                        url,
                    });
                    // context.$ioAnalytics({
                    //   ...createIoConfig(context),
                    //   ...setIoArticleType(context, closest),
                    //   page_title: title,
                    //   page_url: env.HOST + path,
                    //   page_url_canonical: env.HOST + path,
                    // })
                    window.puids = createAdfoxPuids(closest);
                    $page.value = context.app.$page = {
                        ...closest,
                        puids: window.puids,
                    };
                    setSidebarAdfoxTask.perform(0);
                    return;
                }
            }
        });
        function handleReadMoreClick() {
            dispatchEvent(new CustomEvent('togglePullout', { detail: 'waterfall' }));
        }
        /**
         * Handles "unfold" event.
         */
        function handleUnfold(key) {
            articlesFolded.value[key] = false;
            // Force update ;(
            instance.proxy.$forceUpdate();
        }
        /**
         * Sidebar Adfox onComplete
         * @param adfoxItem
         * @param AdfoxOnCompleteArguments
         */
        function onSidebarAdfoxComplete(adfoxItem, { error = {}, state }) {
            if (articleCurrent.value >= unfoldedQuantity.value) {
                return;
            }
            clearTimeout(adfoxCompleteTimeout.value);
            const index = articlesSidebars[articleCurrent.value].items.findIndex((item) => item.id === adfoxItem.id);
            articlesSidebars[articleCurrent.value].itemsVisibility[index] = !(Object.keys(error).length ||
                (state.isCompleted && !state.isRendered));
            adfoxCompleteTimeout.value = setTimeout(() => {
                if (!setSidebarAdfoxTask.isRunning) {
                    setSidebarAdfoxTask.perform();
                }
            }, 500);
        }
        function shouldShowRating(article) {
            const { fields = [], field_values: values = {} } = article.rating || {};
            return Boolean(fields?.length && values);
        }
        const onReadingUpdate = debounce(350, (articleId, readed) => {
            if (bookmarkStore.data[articleId] === undefined) {
                return;
            }
            bookmarkStore.setData(articleId, readed);
            return duck.savedArticle.postSaveArticle({
                articleId,
                readed,
            });
        });
        function performTrafficTasks(isAdfoxError = false) {
            if ($page.value?.type === 'page') {
                return;
            }
            switch (true) {
                // When adblock, the specproject should be inserted second
                case isAdfoxError && articlesRef.value?.length === 1:
                    fetchSpecialTask.cancelAll(isAdfoxError);
                    fetchSpecialTask.perform(isAdfoxError);
                    break;
                // Otherwise specproject should be inserted every fourth
                case !isAdfoxError && (articlesRef.value?.length + 1) % 4 === 0:
                    fetchSpecialTask.cancelAll();
                    fetchSpecialTask.perform();
                    break;
                // Otherwise actual articles should be inserted through one
                default:
                    fetchActualTask.cancelAll();
                    fetchActualTask.perform();
            }
        }
        onBeforeUnmount(() => {
            window.removeEventListener('scroll', handleScroll);
            articlesObserver.value?.disconnect();
            if (search.mark) {
                search.set('mark', '');
            }
        });
        onMounted(() => {
            window.addEventListener('scroll', handleScroll);
            watch(() => user.info?.id, () => {
                for (const article of articles.value) {
                    fetchArticleTask.perform(article.url_alias);
                }
            });
            watch(() => adfoxLoader.error, (isError) => {
                if (isError) {
                    performTrafficTasks(isError);
                }
            }, { immediate: true });
            watch(articlesRef, (refs) => {
                articlesObserver.value = new ResizeObserver(() => {
                    articlesOffsets.value = refs.map((el) => {
                        return el.offsetTop + el.offsetHeight - 80;
                    });
                });
                for (const el of refs) {
                    articlesObserver.value.observe(el);
                }
                performTrafficTasks(adfoxLoader?.error);
            }, {
                immediate: true,
            });
            context.app.router?.beforeEach((_to, _from, next) => {
                fetchArticleTask.cancelAll();
                next();
            });
            setSidebarAdfoxTask.perform(0);
            watch([
                () => a11y.isEnabled,
                () => a11y.fontSize.increment,
                () => a11y.modes.images.isEnabled,
                () => a11y.modes.waterfall.isEnabled,
                () => viewport.breakpoint,
            ], () => setSidebarAdfoxTask.perform());
            watchEffect(() => {
                const article = articles.value[articleCurrent.value];
                if (article) {
                    articlesHistory.setData(article.url_alias);
                }
            });
        });
        return {
            ARTICLE_SIDEBAR_ITEMS,
            a11y,
            adfoxLoader,
            articles,
            articleCurrent,
            articlesFolded,
            articlesOffsets,
            articlesRef,
            articlesSidebars,
            bookmarkStore,
            canLoadFeed,
            fetchArticleTask,
            handleLoaderIntersect,
            handleReadMoreClick,
            handleUnfold,
            isDone,
            isGoogleBot,
            isFolded: computed(() => {
                return (key) => {
                    const { body = [] } = articles.value[key] || {};
                    const limit = createFoldedArticleBody(body).length;
                    return (body || []).length > limit && articlesFolded.value[key];
                };
            }),
            lastArticle,
            lastScrollPosition,
            nextUrl,
            onReadingUpdate,
            onSidebarAdfoxComplete,
            shouldDisableArticleAd,
            shouldShowRating,
            trafficState,
            unfoldedQuantity,
            user,
            waterfall,
        };
    },
    async asyncData(context) {
        const { app, env, error, req, res, route, redirect, query } = context;
        const user = useUser();
        try {
            const routePath = decodeURIComponent(route.path.replace(/^\/|\/$/g, ''));
            let article;
            if (routePath.startsWith('preview')) {
                const { data } = await duck.pub.getPubPreviewToken(routePath.replace('preview/', ''));
                article = data;
            }
            else {
                const { data } = await duck.pub.getPubArticleByAlias({
                    url_alias: routePath,
                });
                article = data;
            }
            try {
                if (article.paywall && user.hasActiveSubscription) {
                    const { data } = await duck.paywall.getPaywallArticleId(String(article.id));
                    if (data.paywall) {
                        Object.assign(article, data);
                    }
                    else {
                        article = data;
                    }
                }
            }
            catch (error) { }
            // Format article
            article.isFirst = true;
            article = formatArticle(article);
            const { changed, erid, id, isBrandvoice, isEventClub, isForbesLife, isForbesWoman, isFranchise, meta_description: metaDescription, meta_title: metaTitle, subtitle = '', title: articleTitle, url_alias: path = '', withVideo, } = article;
            // If it's not a preview link
            if (!routePath.startsWith('preview')) {
                if (path.startsWith('http')) {
                    // Redirect article with external link in url_alias
                    return redirect(301, path);
                }
                const withErid = Boolean(erid) && (!('erid' in query) || erid !== query?.erid);
                const withChangedUrl = path.toLowerCase() !== routePath.toLowerCase();
                if (withErid || withChangedUrl) {
                    // Redirect article with erid or from old url
                    return redirect(301, `/${path}`, { ...query, ...(erid && { erid }) });
                }
            }
            if (process.server) {
                const date = article.changed || article.time || article.created;
                cachePage({ date, req, res });
            }
            let title = (metaTitle || articleTitle) + ' | Forbes.ru';
            const ogTitle = articleTitle;
            let descriptionTail = ` – Forbes Russia – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
            let description = (metaTitle || articleTitle) + descriptionTail;
            let ogDescription = articleTitle + descriptionTail;
            if (isBrandvoice) {
                // title = (metaTitle || articleTitle) + ' | Brandvoice | Forbes.ru'
                title = (metaTitle || articleTitle) + ' | Блоги | Forbes.ru';
                try {
                    const { data: tax } = await duck.pub.getPubTax({
                        'list[limit]': 1,
                        'tax[url]': path.replace(/\/\d+.+/g, ''),
                    });
                    article.tax = tax;
                }
                catch (error) {
                    console.error(`Failed to get tax`, error);
                }
            }
            if (isFranchise) {
                title = `Купить франшизу ${articleTitle} на сайте Forbes.ru`;
                descriptionTail = `– купить на сайте Forbes Russia – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
                description = `Франшиза ${articleTitle} ${descriptionTail}`;
                ogDescription = `Франшиза ${ogTitle} ${descriptionTail}`;
            }
            if (isForbesLife) {
                title = (metaTitle || articleTitle) + ' | Forbes Life';
                descriptionTail = ` – Forbes Life – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
                description = (metaTitle || articleTitle) + descriptionTail;
                ogDescription = ogTitle + descriptionTail;
            }
            if (isForbesWoman) {
                title = (metaTitle || articleTitle) + ' | Forbes Woman';
                descriptionTail = ` – Forbes Woman – одно из наиболее авторитетных и известных финансово-экономических изданий в мире`;
                description = (metaTitle || articleTitle) + descriptionTail;
                ogDescription = ogTitle + descriptionTail;
            }
            if (isEventClub) {
                title = (metaTitle || articleTitle) + ' | Forbes Сlub';
                descriptionTail = ` – Forbes Club`;
                description = (metaTitle || articleTitle) + descriptionTail;
                ogDescription = ogTitle + descriptionTail;
                article.sections = [
                    { id: 0, name: 'Forbes Club', url: 'club' },
                    { id: 1, name: 'Мероприятия', url: 'club/events' },
                ];
            }
            if (withVideo) {
                title = (metaTitle || articleTitle) + ' | Видео Forbes.ru';
                descriptionTail = ` – о бизнесе, миллиардерах, новостях, финансах и инвестициях смотрите на Forbes.ru`;
                description = (metaTitle || articleTitle) + descriptionTail;
                ogDescription = ogTitle + descriptionTail;
            }
            if ((subtitle || '').trim() !== '') {
                description = subtitle?.slice(0, 250);
                ogDescription = description;
            }
            if ((metaDescription || '').trim() !== '') {
                description = metaDescription?.slice(0, 250) || '';
            }
            const canonical = `${env.HOST}/${path}`;
            const head = {
                canonical: erid ? `${canonical}?erid=${erid}` : canonical,
                description,
                image: createShareImage({
                    article: { id, changed },
                    env,
                }),
                imageVK: createShareImage({
                    article: { id, changed },
                    env,
                    isVk: true,
                }),
                ogDescription,
                ogTitle,
                title,
                url: `${env.HOST}${route.fullPath}`,
            };
            context.$page = app.$page = {
                ...article,
                title,
                puids: createAdfoxPuids(article),
            };
            return {
                $page: context.$page,
                articles: [article],
                nextUrl: article.next_url || article.rating?.next_url || '',
                head,
                jsonLdSchema: createArticleSchema.call(context, article),
            };
        }
        catch (e) {
            console.error(e);
            error({ statusCode: 404 });
        }
    },
    head() {
        return createHead(this.head, { shouldUseTitleTemplate: false });
    },
});
